import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { IContext } from './columns'
import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IAppManagement } from '~/types/application-management'

import columns from './columns'
import SetAppModal from './SetAppModal'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useTouchDown } from '~/hooks/useTouchDown'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useAppsManagementListQuery } from '~/queries/application-management'

const AppsManagement = (): ReactElement => {
  /** Local state */

  const applicationWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.VERSION.APPLICATION.WRITE])

  const navigate = useNavigate()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [isModalOpen, setModalOpen] = useState(false)

  const { isTouchDown, setIsTouchDown } = useTouchDown('Meta')

  /** Queries & Mutations */

  const { data: appsListing } = useAppsManagementListQuery()

  /** Effects */

  useDocumentTitle('Apps management')

  /** Handlers */

  const handleModalState = (isOpen: boolean) => () => setModalOpen(isOpen)

  const handleRowClicked = (evt: RowClickedEvent<IAppManagement>) => {
    if (gridRef.current?.api?.getFocusedCell()?.column.getColId() !== 'actions' && evt.data) {
      if (isTouchDown) {
        window.open(`/apps-management/${evt.data.app_key}`, '_blank', 'rel=noopener noreferrer')
        setIsTouchDown(false)
      } else {
        navigate(`/apps-management/${evt.data.app_key}`)
      }
    }
  }

  const handleRedirect = useCallback((path: string) => navigate(path), [navigate])

  const context: IContext = useMemo(() => ({ handleRedirect }), [handleRedirect])

  /** Render */

  return (
    <Layout className="apps-management-view" withPadding>
      <ViewTitle className="layout-view-title">Apps management</ViewTitle>
      <ContentLayout>
        <Section className="section">
          <TableWrapper
            addButton
            addButtonDisabled={!applicationWriteAuthorized}
            addButtonLabel="New application"
            addButtonTooltip={!applicationWriteAuthorized ? 'unauthorized' : undefined}
            className="apps-management-table with-min-height"
            columnDefs={columns}
            context={context}
            entriesName="new apps"
            onAddButtonClick={handleModalState(true)}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            rowData={appsListing}
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </Section>
        <SetAppModal isOpen={isModalOpen} onClose={handleModalState(false)} />
      </ContentLayout>
    </Layout>
  )
}

export default AppsManagement
